import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

export type DeviceClass = 'mobile' | 'tablet' | 'desktop';

const MOBILE_MAX = 767;
const TABLET_MAX = 1023;

function classifyWidth(w: number): DeviceClass {
  if (w <= MOBILE_MAX) return 'mobile';
  if (w <= TABLET_MAX) return 'tablet';
  return 'desktop';
}

interface DeviceProfileValue {
  deviceClass: DeviceClass;
  viewportWidth: number;
  isPortrait: boolean;
  isTouchCoarse: boolean;
}

const DeviceProfileContext = createContext<DeviceProfileValue | null>(null);

function readTouchCoarse(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.matchMedia('(pointer: coarse)').matches;
  } catch {
    return (navigator.maxTouchPoints ?? 0) > 0;
  }
}

export const DeviceProfileProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [viewportWidth, setViewportWidth] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth : 1280
  );
  const [isPortrait, setIsPortrait] = useState(() =>
    typeof window !== 'undefined' ? window.innerHeight > window.innerWidth : false
  );
  const [isTouchCoarse, setIsTouchCoarse] = useState(readTouchCoarse);

  const refresh = useCallback(() => {
    if (typeof window === 'undefined') return;
    setViewportWidth(window.innerWidth);
    setIsPortrait(window.innerHeight > window.innerWidth);
    setIsTouchCoarse(readTouchCoarse());
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    let t: ReturnType<typeof setTimeout> | undefined;
    const schedule = () => {
      if (t) clearTimeout(t);
      t = setTimeout(() => {
        refresh();
        t = undefined;
      }, 100);
    };
    window.addEventListener('resize', schedule);
    window.addEventListener('orientationchange', schedule);
    const mqCoarse = window.matchMedia('(pointer: coarse)');
    const onMq = () => setIsTouchCoarse(readTouchCoarse());
    mqCoarse.addEventListener?.('change', onMq);
    refresh();
    return () => {
      window.removeEventListener('resize', schedule);
      window.removeEventListener('orientationchange', schedule);
      mqCoarse.removeEventListener?.('change', onMq);
      if (t) clearTimeout(t);
    };
  }, [refresh]);

  const value = useMemo<DeviceProfileValue>(
    () => ({
      deviceClass: classifyWidth(viewportWidth),
      viewportWidth,
      isPortrait,
      isTouchCoarse,
    }),
    [viewportWidth, isPortrait, isTouchCoarse]
  );

  return <DeviceProfileContext.Provider value={value}>{children}</DeviceProfileContext.Provider>;
};

export function useDeviceProfile(): DeviceProfileValue {
  const ctx = useContext(DeviceProfileContext);
  if (!ctx) {
    return {
      deviceClass: 'desktop',
      viewportWidth: 1280,
      isPortrait: false,
      isTouchCoarse: false,
    };
  }
  return ctx;
}
